<template>
  <div class="row">
    <!-- Spinner ładowania (pokazuje się, gdy loading = true) -->
    <spinner-element :loading="loading" />
    <!-- Wyświetlanie danych po załadowaniu aktywnej zakładki -->
    <div class="row" v-if="activeTabData">
      <div class="col-12" id="details">
        <!-- Sekcja z danymi o grupach VMS -->
        <div class="row" id="road-info" v-if="!loading">
          <!-- Dla każdej grupy VMS (np. oddzielne odcinki dróg) -->
          <div v-for="(group, groupId) in localGroupStatuses" :key="groupId" class="content">
            <!-- Pasek tytułowy grupy oraz przycisk rozwiń/zwiń -->
            <div class="d-flex justify-content-between align-items-center mb-2 table-container">
              <h5>{{ localGroupDescriptions[groupId] }}</h5>
              <button @click="toggleGroup(groupId)">
                {{ expandedGroups[groupId] ? t('collapse') : t('expand') }}
              </button>
            </div>
            <!-- Rozwinięcie grupy: Tabela ze znakami -->
            <div v-if="expandedGroups[groupId]">
              <table class="center-aligned table table-bordered">
                <tbody>
                  <tr>
                    <!-- Tabela z listą znaków VMS -->
                    <td>
                      <table class="center-aligned table table-striped">
                        <thead>
                          <tr>
                            <th class="lp">{{ t('ordinal-number') }}</th>
                            <th class="road_no">{{ t('short-road-no') }}</th>
                            <th class="chainage">{{ t('chainage') }}</th>
                            <th class="ip_address">{{ t('ip-address') }}</th>
                            <th class="status">{{ t('status') }}</th>
                            <th class="sign_enabled">{{ t('sign-enabled') }}</th>
                            <th class="sign_image">{{ t('sign-image') }}</th>
                            <th class="variant">{{ t('selected-variant') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <!-- Dla każdego znaku w grupie -->
                          <tr v-for="(vms, vmsIndex) in group" :key="vms.id">
                            <td>{{ vmsIndex + 1 }}</td>
                            <td>{{ vms.vms.roadNumber }}</td>
                            <td>{{ vms.vms.chainage }}</td>
                            <td>{{ vms.vms.ip }}</td>
                            <td>
                              <!-- Ikona statusu OK/Niedostępny -->
                              <img v-if="vms.vmsStatus.status === 'OK'" :src="require('@/assets/icons/dostepny.png')" />
                              <img v-else-if="vms.vmsStatus.status && vms.vmsStatus.status.startsWith('Unable')" :src="require('@/assets/icons/niedostepny.png')" />
                            </td>
                            <td>
                              <!-- Status: czy znak wyświetla treść -->
                              {{ vms.vmsStatus.available ? (vms.vmsStatus.contentDisplayed ? t('yes').toUpperCase() : t('no').toUpperCase()) : t('no-data') }}
                            </td>
                            <td>
                              <!-- Podgląd obrazu na znaku (jeśli dostępny) -->
                              <img v-if="vms.image" :src="vms.image" alt="Obraz na znaku" class="img-fluid" />
                              <span v-else>{{ t('no-image') }}</span>
                            </td>
                            <td>{{ vms.selectedContent || t('not-selected') }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </td>

                    <!-- Panel do wyboru wariantu oraz włącz/wyłącz wszystkie znaki w grupie -->
                    <td class="button-container">
                      <div style="margin-bottom:5px;">{{ t('select-variant') }}</div>
                      <!-- Wybierz wariant treści do wyświetlenia na znakach -->
                      <select class="vmsOnOffButton vmsCheckVariant" style="margin-bottom:5px;" @change="handleVariantSelect($event, groupId)">
                        <option value="0">----</option>
                        <option
                          v-for="(contentVariant, index) in getUniqueVariants(group) || []"
                          :key="contentVariant.id"
                          :value="index + 1"
                        >
                          {{ contentVariant.variant }}
                        </option>
                      </select>
                      <!-- Przycisk: Włącz wszystkie znaki w grupie -->
                      <button
                        class="vmsOnOffButton enableGroup"
                        :disabled="!canEnableOrDisableGroup(group)"
                        @click="toggleAllGroupSigns('on', groupId)"
                      >
                        {{ t('enable-all') }}
                      </button>
                      <!-- Przycisk: Wyłącz wszystkie znaki w grupie -->
                      <button
                        class="vmsOnOffButton disableGroup"
                        :disabled="!canEnableOrDisableGroup(group)"
                        @click="toggleAllGroupSigns('off', groupId)"
                      >
                        {{ t('disable-all') }}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Informacja, gdy nie ma danych -->
    <div class="row vehicle-panel" v-if="noData">
      <div class="col-3" id="no-data-info">
        <div class="row details-row-field">
          {{ t('no-data-for-selected-station') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Importy bibliotek i komponentów
import moment from "moment";
import Vms from "@/services/Vms";
import { useVmsFilterStore } from "@/stores/vmsFilters";
import SpinnerElement from "./SpinnerElement.vue";
require("../assets/css/viver-style.css");

moment.locale("pl");

export default {
  name: "VmsData",
  components: {
    SpinnerElement,
  },
  props: {
    // Przekazywane statusy grup z rodzica
    groupStatuses: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    // Opisy grup (np. nazwy odcinków)
    groupDescriptions: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  setup() {
    // Pinia store (filtrowanie znaków)
    const filtersStore = useVmsFilterStore();
    return { filtersStore };
  },
  data() {
    return {
      loading: false,                // Czy trwa ładowanie danych
      vmsData: [],                   // Dane VMS (nieużywane)
      activeTabData: { signs: [] },  // Dane aktywnej zakładki
      noData: false,                 // Brak danych
      allEnabled: false,             // Czy wszystkie znaki aktywne (nieużywane)
      firstGroupVariants: [],        // Warianty w pierwszej grupie
      expandedGroups: {},            // Obiekt: które grupy są rozwinięte
      localGroupStatuses: {},        // Statusy grup VMS (z propsów lub API)
      localGroupDescriptions: {},    // Opisy grup (z propsów lub API)
    };
  },
  watch: {
    // Synchronizacja lokalnych danych z propsami (groupStatuses/groupDescriptions)
    groupStatuses: {
      immediate: true,
      handler(newVal) {
        this.localGroupStatuses = JSON.parse(JSON.stringify(newVal));
      },
    },
    groupDescriptions: {
      immediate: true,
      handler(newVal) {
        this.localGroupDescriptions = { ...newVal };
      },
    },
  },
  methods: {
    // Pobieranie danych z API, jeśli nie ma nic przekazanego w propsach
    async loadDataIfPropsEmpty() {
      this.loading = true;
      try {
        const data = await Vms.getAllGroupStatus();

        // Jeśli poprawny format (dwie tablice: statusy i opisy grup)
        if (Array.isArray(data) && data.length === 2) {
          const groupStatuses = {};
          const groupDescriptions = {};
          const vmsByGroup = data[0]; // {1: [...], 4: [...], 5: [...]}
          const groupNames = data[1]; // {1: '...', 4: '...', 5: '...'}

          Object.entries(vmsByGroup).forEach(([id, vmsArray]) => {
            groupStatuses[id] = vmsArray;
            groupDescriptions[id] = groupNames[id] || `Grupa ${id}`;
          });

          this.localGroupStatuses = groupStatuses;
          this.localGroupDescriptions = groupDescriptions;
          this.noData = Object.keys(groupStatuses).length === 0;
          this.fetchGroupStatuses();
        } else {
          this.noData = true;
        }
      } catch (err) {
        console.error("Błąd przy automatycznym pobieraniu danych:", err);
        this.noData = true;
      } finally {
        this.loading = false;
      }
    },
    // Czy można włączyć/wyłączyć grupę (czy każdy znak ma wybrany wariant i obraz)
    canEnableOrDisableGroup(group) {
      return group.every(vms => vms.image && vms.selectedContent);
    },
    // Włącz/wyłącz wszystkie znaki w grupie
    async toggleAllGroupSigns(action, groupId) {
      const group = this.localGroupStatuses[groupId];
      try {
        if (action === 'on') {
          const selectedVariant = this.getSelectedVariantForGroup(group);
          if (selectedVariant) {
            await Vms.enableVms(groupId, selectedVariant);
          }
        } else if (action === 'off') {
          await Vms.disableVms(groupId);
        }
        await this.fetchGroupStatuses();
      } catch (err) {
        console.error('Błąd przy przełączaniu znaków:', err);
      }
    },
    // Jaki wariant jest wybrany dla grupy?
    getSelectedVariantForGroup(group) {
      const firstSelected = group.find(vms => vms.selectedContent);
      return firstSelected?.selectedContent || null;
    },
    // Rozwiń/zwiń daną grupę
    toggleGroup(groupId) {
      this.expandedGroups[groupId] = !this.expandedGroups[groupId];
    },
    // Odśwież dane o statusach grup (np. po kliknięciu włącz/wyłącz)
    async fetchGroupStatuses() {
      this.loading = true;
      this.noData = false;
      try {
        if (Object.keys(this.groupStatuses).length > 0) {
          this.localGroupDescriptions = this.groupDescriptions;
          this.localGroupStatuses = this.groupStatuses;
        }
        if (Object.keys(this.localGroupStatuses).length > 0) {
          const firstGroupId = Object.keys(this.localGroupStatuses)[0];
          this.firstGroupVariants = this.localGroupStatuses[firstGroupId][0].contentVariants || [];
        } else {
          this.firstGroupVariants = [];
        }
        this.loading = false;
        // Domyślnie rozwijaj pierwszą grupę
        this.expandedGroups = {};
        let i = 0;
        for (const groupId in this.localGroupStatuses) {
          this.expandedGroups[groupId] = i === 0;
          i++;
        }
      } catch (err) {
        console.error('Błąd podczas pobierania danych VMS:', err);
        this.loading = false;
        this.noData = true;
      }
    },
    // Zwraca listę unikalnych wariantów (treści) możliwych do wyświetlenia w grupie
    getUniqueVariants(group) {
      const seenVariants = new Set();
      const uniqueVariants = [];
      group.forEach(vms => {
        (vms.contentVariants || []).forEach(variant => {
          const variantValue = variant?.variant;
          if (variantValue && !seenVariants.has(variantValue)) {
            seenVariants.add(variantValue);
            uniqueVariants.push(variant);
          }
        });
      });
      return uniqueVariants.sort((a, b) => a.variant - b.variant);
    },
    // Wybranie wariantu (typowej treści) do wyświetlenia na znakach w grupie
    async handleVariantSelect(event, groupId) {
      const selectedIndex = event.target.value;
      const group = this.localGroupStatuses[groupId];
      // 0 = brak wyboru
      if (selectedIndex === "0") {
        for (const vms of group) {
          vms.image = null;
          vms.selectedContent = null;
        }
        return;
      }
      // Zaznacz wybrany wariant dla każdego znaku
      const uniqueVariants = this.getUniqueVariants(group);
      const selectedVariantGlobal = uniqueVariants[selectedIndex - 1];
      if (!selectedVariantGlobal) return;
      for (const vms of group) {
        const matchingVariant = (vms.contentVariants || []).find(
          cv => cv.variant === selectedVariantGlobal.variant
        );
        if (matchingVariant && matchingVariant.content?.id) {
          const image = await this.fetchImageForSign(matchingVariant.content.id);
          vms.image = image;
          vms.selectedContent = matchingVariant.variant;
        } else {
          vms.image = null;
          vms.selectedContent = null;
        }
      }
    },
    // Pobierz obraz pod wariantem (do podglądu)
    async fetchImageForSign(id) {
      try {
        const imageBase64 = await Vms.getImageForSign(id);
        return imageBase64 ? imageBase64 : null;
      } catch (error) {
        console.error('Błąd podczas pobierania obrazu:', error);
        return null;
      }
    },
    // Ustawianie filtra w store Pinia (filtrowanie znaków)
    setFilter(data) {
      this.filtersStore.addItem("signs", data);
    },
    // Włącz/wyłącz wszystkie znaki dla wybranych lokalizacji (nieużywane w tym widoku)
    async toggleAllSigns(status) {
      this.loading = true;
      const selectedLocationIds = this.filtersStore.filters.locations;
      if (!selectedLocationIds.length) {
        this.loading = false;
        this.noData = true;
        return;
      }
      if (status === "Wyłączony") {
        try {
          const data = await Vms.getAllGroupStatus();
          const targetData = selectedLocationIds[0] === 110 ? data[0] : data[1];
          await Vms.enableVms(targetData.group.id, targetData.vms[0].contentVariants[0].variant);
        } catch (error) {
          console.error("Błąd podczas zmiany statusu znaków:", error);
        }
      } else {
        try {
          const data = await Vms.getAllGroupStatus();
          const targetData = selectedLocationIds[0] === 110 ? data[0] : data[1];
          await Vms.disableVms(targetData.group.id);
        } catch (error) {
          console.error("Błąd podczas zmiany statusu znaków:", error);
        }
      }
      Vms.getAllGroupStatus();
      await this.fetchData();
    },
  },
  mounted() {
    // Jeżeli nie przekazano propsów - pobierz dane z API
    if (
      !this.groupStatuses ||
      Object.keys(this.groupStatuses).length === 0
    ) {
      this.loadDataIfPropsEmpty();
    } else {
      this.fetchGroupStatuses();
    }
  },
};
</script>

<style scoped src="../assets/css/viver-style.css"></style>

<style scoped>
.row {
  margin: 0;
}

.content {
  margin-top: 25px;
  background-color: white;
}

.table-container button {
  padding: 5px 40px;
  margin-top: 10px;
  margin-right: 1px;
  background-color: #e0e0e0;
  cursor: pointer;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  border: none;
}

h5 {
  margin-top: 13px;
  font-size: 18px !important;
  font-weight: bold;
}

.details-row-field {
  font-family: "Ubuntu", sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
}

.details-row-field-b {
  font-weight: bold;
  font-family: "Ubuntu", sans-serif;
  font-style: normal;
  font-size: 14px;
}
.vehicle-panel {
  margin-top: 15px;
}
#road-info .row > div:first-child {
  border: 0;
}

table {
  width: 100%;
}

thead, tbody {
  width: 100%;
}

.button-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.vmsOnOffButton {
  width: 75%;
}

.lp {
  width: 5%;
}

.road_no {
  width: 5%;
}

.chainage {
  width: 10%;
}

.ip_address {
  width: 20%;
}

.status {
  width: 10%;
}

.sign_enabled {
  width: 10%;
}

.sign_image {
  width: 15%;
}

.variant {
  width: 15%;
}
</style>
